/** Create / edit dialog for one translation glossary term (react-hook-form + zod).
 *
 *  `keep` terms are kept verbatim by every machine translation of the operator; `translate`
 *  terms are replaced by the operator's preferred translation per target language (one row
 *  per language). The operator is fixed once the term exists. Last-writer-wins. */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  create as createTerm,
  update as updateTerm,
  type GlossaryTerm,
  type GlossaryTermWrite,
} from '@/api/translationGlossary';
import { AppDialog } from '@/components/AppDialog';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { HelpLink } from '@/components/HelpLink';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { usePermissions } from '@/hooks/usePermissions';
import { singlePageTenant } from '@/scope/pageTenants';

import { COMMON_LANGUAGES } from '../tags/schema/vocabulary';
import { GLOSSARY_HELP } from './fieldHelp';

const LANG_RE = /^[a-z]{2}$/;

const FormSchema = z
  .object({
    term: z.string().trim().min(1, 'Term is required').max(255),
    kind: z.enum(['keep', 'translate']),
    source_lang: z
      .string()
      .trim()
      .refine((v) => v === '' || LANG_RE.test(v), 'A 2-letter language code, or empty for any'),
    translations: z.array(
      z.object({
        lang: z.string().trim().regex(LANG_RE, '2-letter code'),
        text: z.string().trim().min(1, 'Required').max(255),
      }),
    ),
    case_sensitive: z.boolean(),
    enabled: z.boolean(),
    notes: z.string(),
  })
  .refine((v) => v.kind === 'keep' || v.translations.length > 0, {
    path: ['translations'],
    message: 'Add at least one preferred translation.',
  })
  .refine((v) => new Set(v.translations.map((t) => t.lang)).size === v.translations.length, {
    path: ['translations'],
    message: 'One translation per language.',
  });

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = {
  term: '',
  kind: 'keep',
  source_lang: '',
  translations: [],
  case_sensitive: true,
  enabled: true,
  notes: '',
};

function toDefaults(term: GlossaryTerm | null): FormValues {
  if (!term) return EMPTY;
  return {
    term: term.term,
    kind: term.kind,
    source_lang: term.source_lang,
    translations: Object.entries(term.translations).map(([lang, text]) => ({ lang, text })),
    case_sensitive: term.case_sensitive,
    enabled: term.enabled,
    notes: term.notes,
  };
}

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  term: GlossaryTerm | null;
  /** The page's operators (D9), for create: the one is pre-selected, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function GlossaryTermDialog({ open, term, pageTenants, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = term !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.translation_glossary_term',
    objectPk: term?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['translation_glossary'] }),
  });
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [tenantError, setTenantError] = useState('');

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema), defaultValues: toDefaults(term) });
  const { fields, append, remove } = useFieldArray({ control, name: 'translations' });

  useEffect(() => {
    if (!open) return;
    history.reset();
    reset(toDefaults(term));
    setTenant(
      term ? { id: term.tenant.id, name: term.tenant.name, code: term.tenant.code } : (singlePageTenant(pageTenants)),
    );
    setTenantError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, term?.id]);

  const mutation = useMutation({
    mutationFn: (body: GlossaryTermWrite) => (isEdit ? updateTerm(term.id, body) : createTerm(body)),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Glossary term updated.' : 'Glossary term created.', 'success');
      qc.invalidateQueries({ queryKey: ['translation_glossary'] });
      onClose();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    if (!isEdit && !tenant) {
      setTenantError('Choose the operator this glossary belongs to.');
      return;
    }
    const body: GlossaryTermWrite = {
      term: v.term.trim(),
      kind: v.kind,
      source_lang: v.source_lang.trim(),
      translations:
        v.kind === 'translate' ? Object.fromEntries(v.translations.map((t) => [t.lang.trim(), t.text.trim()])) : {},
      case_sensitive: v.case_sensitive,
      enabled: v.enabled,
      notes: v.notes,
    };
    if (!isEdit && tenant) body.tour_operator = tenant.id;
    mutation.mutate(body);
  });

  const kind = watch('kind');
  const translationsError = (errors.translations as { message?: string } | undefined)?.message;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title={
        <>
          {isEdit ? `Edit glossary term · ${term.term}` : 'New glossary term'}{' '}
          <HelpLink story="translation-glossary" newTab />
        </>
      }
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create term'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
        <Box component="form" onSubmit={submit} noValidate sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ flexGrow: 1 }}>
              {isEdit ? (
                // A term never moves to another operator.
                <TextField label="Operator" size="small" fullWidth disabled value={term.tenant.code || term.tenant.name} />
              ) : (
                <TenantPicker
                  value={tenant}
                  featured={isEdit ? undefined : pageTenants}
                  onChange={(v) => {
                    setTenant(v);
                    setTenantError('');
                  }}
                  label="Operator"
                  placeholder="Choose an operator"
                />
              )}
              {tenantError && (
                <Typography variant="caption" color="error">
                  {tenantError}
                </Typography>
              )}
            </Box>
            <FieldHelp help={GLOSSARY_HELP.operator} />
          </Box>

          <TextField
            {...register('term')}
            label="Term"
            size="small"
            fullWidth
            error={Boolean(errors.term)}
            helperText={errors.term?.message ?? 'As it appears in the source text.'}
            InputProps={{ endAdornment: <FieldHelp help={GLOSSARY_HELP.term} /> }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Controller
              control={control}
              name="kind"
              render={({ field }) => (
                <TextField
                  select
                  label="Kind"
                  size="small"
                  fullWidth
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  <MenuItem value="keep">Keep verbatim</MenuItem>
                  <MenuItem value="translate">Preferred translations</MenuItem>
                </TextField>
              )}
            />
            <FieldHelp help={GLOSSARY_HELP.kind} />
            <TextField
              {...register('source_lang')}
              label="Source language"
              size="small"
              sx={{ width: 200, flexShrink: 0 }}
              placeholder="any"
              error={Boolean(errors.source_lang)}
              helperText={errors.source_lang?.message}
              InputProps={{ endAdornment: <FieldHelp help={GLOSSARY_HELP.sourceLang} /> }}
            />
          </Stack>

          {kind === 'translate' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preferred translations
              </Typography>
              <Stack spacing={1}>
                {fields.map((f, index) => (
                  <Stack key={f.id} direction="row" spacing={1} alignItems="flex-start">
                    <Controller
                      control={control}
                      name={`translations.${index}.lang`}
                      render={({ field, fieldState }) => (
                        <TextField
                          select
                          size="small"
                          label="Language"
                          sx={{ width: 170, flexShrink: 0 }}
                          value={field.value}
                          error={Boolean(fieldState.error)}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          {COMMON_LANGUAGES.map((l) => (
                            <MenuItem key={l.code} value={l.code}>
                              {l.label} ({l.code})
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <TextField
                      {...register(`translations.${index}.text`)}
                      size="small"
                      label="Translation"
                      fullWidth
                      error={Boolean(errors.translations?.[index]?.text)}
                      helperText={errors.translations?.[index]?.text?.message}
                    />
                    <IconButton size="small" color="error" onClick={() => remove(index)} aria-label="Remove translation">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              {translationsError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {translationsError}
                </Typography>
              )}
              <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => append({ lang: '', text: '' })}>
                Add language
              </Button>
            </Box>
          )}

          <Stack direction="row" spacing={2}>
            <Controller
              control={control}
              name="case_sensitive"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label={labelWithHelp('Case sensitive', GLOSSARY_HELP.caseSensitive)}
                />
              )}
            />
            <Controller
              control={control}
              name="enabled"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Enabled"
                />
              )}
            />
          </Stack>

          <TextField {...register('notes')} label="Notes" size="small" fullWidth multiline minRows={2} />
        </Box>
      )}
    </AppDialog>
  );
}
